import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Building2, Calendar, User } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { formatCurrency, formatRelativeTime, getInitials } from '@/lib/formatters';
import type { Deal } from '@/types/table';
import { useNavigate } from 'react-router-dom';

interface KanbanCardProps {
  deal: Deal;
  isDragging?: boolean;
}

export function KanbanCard({ deal, isDragging }: KanbanCardProps) {
  const navigate = useNavigate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: deal.id,
    data: deal,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        'cursor-grab p-3 active:cursor-grabbing',
        (isDragging || isSortableDragging) && 'opacity-50 shadow-lg',
      )}
      onClick={() => navigate(`/deals/${deal.id}`)}
      {...attributes}
      {...listeners}
    >
      <h4 className="text-sm font-medium leading-tight mb-2">{deal.title}</h4>
      <p className="text-sm font-semibold text-primary mb-2">
        {formatCurrency(deal.value, deal.currency)}
      </p>

      <div className="space-y-1">
        {deal.company && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Building2 className="h-3 w-3" />
            <span className="truncate">{deal.company.name}</span>
          </div>
        )}
        {deal.expectedCloseDate && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{formatRelativeTime(deal.expectedCloseDate)}</span>
          </div>
        )}
      </div>

      {deal.assignedTo && (
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Avatar className="h-5 w-5">
              <AvatarFallback className="text-[9px]">
                {getInitials(deal.assignedTo.firstName, deal.assignedTo.lastName)}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground">
              {deal.assignedTo.firstName}
            </span>
          </div>
          <User className="h-3 w-3 text-muted-foreground" />
        </div>
      )}
    </Card>
  );
}
